// Chat transport — HOW the stream is opened. This is the per-host seam: the
// widget passes a header-auth transport (makeHeaderStream below); the dashboard
// passes its own bearer-token + 401-refresh streamer. useChat owns the reducer;
// it never does its own fetch.

import { parseSSE } from './sse'

/** Request body for POST /chat/stream. */
export interface StreamBody {
  message?: string | null
  thread_id?: string | null
  resume_value?: unknown
  /** Structured entry (incidents "Investigate in chat"): the backend routes
   *  this turn straight into the alert-investigation flow for that incident,
   *  bypassing free-text intent classification. */
  incident_id?: string | null
}

/** One decoded SSE frame handed to the reducer. */
export interface SSEFrame {
  event: string
  data: unknown
}

/**
 * Opens the stream and calls `onEvent` for each decoded SSE frame until the
 * stream ends (or `signal` aborts). Auth/headers/refresh all live in here, so
 * each host supplies its own.
 */
export type ChatTransport = (
  body: StreamBody,
  onEvent: (frame: SSEFrame) => void,
  signal: AbortSignal,
) => Promise<void>

// Watchdog: a healthy stream delivers bytes regularly — real events, or the
// server's `: ping` heartbeat every few seconds. If nothing arrives for this
// long the connection is dead (a proxy dropped it, the network dropped out), so
// we abort instead of blocking on read() forever and stranding the UI on
// "Thinking…". Comfortably larger than the server heartbeat so it only trips on
// a genuine stall, never on normal model latency.
const STALL_TIMEOUT_MS = 30_000

// reader.read() that rejects if no chunk arrives within `ms`. On timeout the
// caller cancels the reader, releasing the still-pending native read.
function readWithTimeout(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  ms: number,
): Promise<ReadableStreamReadResult<Uint8Array>> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('The connection stalled — please try again.')),
      ms,
    )
    reader.read().then(
      (r) => {
        clearTimeout(timer)
        resolve(r)
      },
      (e) => {
        clearTimeout(timer)
        reject(e as Error)
      },
    )
  })
}

export interface HeaderStreamOptions {
  /** Absolute URL of the streaming endpoint (POST, SSE response). */
  api: string
  /** Static request headers — host auth context (e.g. client-id/username). */
  headers?: Record<string, string>
}

/**
 * A simple transport that POSTs with static headers and streams the SSE
 * response. Used by the widget (embedded in an already-authenticated host page,
 * so it carries client-id/username headers rather than a refreshable token).
 *
 * Honors the backend's terminal `done` frame — cancels the reader instead of
 * waiting for socket close, so a buffering proxy can't strand the UI.
 */
export function makeHeaderStream({ api, headers = {} }: HeaderStreamOptions): ChatTransport {
  return async (body, onEvent, signal) => {
    const res = await fetch(api, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...headers },
      body: JSON.stringify(body),
      signal,
    })
    if (!res.ok || !res.body) {
      let detail = `HTTP ${res.status}`
      try {
        const j = await res.json()
        if (j?.detail) detail = j.detail
      } catch {
        // non-JSON error body — keep the HTTP status detail
      }
      throw new Error(detail)
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    for (;;) {
      let result: ReadableStreamReadResult<Uint8Array>
      try {
        result = await readWithTimeout(reader, STALL_TIMEOUT_MS)
      } catch (err) {
        // Stalled, or the underlying read rejected — release the reader and
        // surface it so the turn ends instead of hanging on "Thinking…".
        await reader.cancel().catch(() => {})
        throw err
      }
      const { done, value } = result
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const { events, rest } = parseSSE(buffer)
      buffer = rest
      for (const frame of events) onEvent(frame)
      if (events.some((f) => f.event === 'done')) {
        await reader.cancel()
        return
      }
    }
    // Flush any trailing frame that arrived without its blank-line terminator.
    buffer += decoder.decode()
    const { events } = parseSSE(buffer)
    for (const frame of events) onEvent(frame)
  }
}
